import * as React from 'react';
import TextField from 'material-ui/TextField';
import SelectField from 'material-ui/SelectField';
import MenuItem from 'material-ui/MenuItem';
import { black } from 'material-ui/styles/colors';
import Phone from 'react-phone-number-input';
import { CountryCode, CountryInfo, ProvinceCode, ProvinceInfo } from './geo.types';
import { AddressOnlyInfo, AddressOnlyInfoError } from './address.types';
import { isValidPostalCode, isValidPhone } from '../util/Validation';
import { getCountries, getProvinces } from './geo';
import { DEFAULT_COUNTRY_SELECTION, DEFAULT_PROVINCE_SELECTION } from '../app/app.const';
import { formatPostalCode } from '../util/address.util';
import { BusinessState } from '../business/business.types';
import { AuthState } from 'src/auth/auth.types';
import { cannotWrite, cannotUpdate } from '../util/permission.util';
import { BUSINESS } from '../account/Constants';
import { AdminState } from 'src/admin/admin.types';

const MAX_LENGTH_FOR_DB = 20;

const fieldStyles = {
  rootStyle: {
    cursor: 'text'
  },
  textStyle: {
    cursor: 'text',
    color: black
  }
};

interface Props {
  businessState: BusinessState;
  authState?: AuthState;
  adminState?: AdminState;
  addressOnlyInfo: AddressOnlyInfo;
  addressOnlyInfoError: AddressOnlyInfoError;
  addressOnlyInfoChangeHandler?: Function;
  mode?: string;
  validator?: (phone: string) => Promise<Response>;
  isBusinessRegistration: boolean;
  isBusinessInfo?: boolean;
}

interface States {
  addressOnlyInfo: AddressOnlyInfo;
  addressOnlyInfoError: AddressOnlyInfoError;
  countriesList: CountryInfo[];
  provincesList: ProvinceInfo[];
}

class AddressOnlyComponent extends React.Component<Props, States> {

  constructor(props: Props) {
    super(props);

    this.state = {
      addressOnlyInfo: props.addressOnlyInfo,
      addressOnlyInfoError: props.addressOnlyInfoError,
      countriesList: getCountries(),
      provincesList: props.addressOnlyInfo.country ? getProvinces(props.addressOnlyInfo.country) : []
    };

    this.onAddressChange = this.onAddressChange.bind(this);
    this.onCityChange = this.onCityChange.bind(this);
    this.onCountryChange = this.onCountryChange.bind(this);
    this.onProvinceChange = this.onProvinceChange.bind(this);
    this.onPostalCodeChange = this.onPostalCodeChange.bind(this);
    this.addressOnlyInfoChangeHandler = this.addressOnlyInfoChangeHandler.bind(this);
  }

  componentWillReceiveProps(nextProps: Props) {
    if (this.props !== nextProps) {
      this.setState({
        provincesList: (nextProps.addressOnlyInfo.country)
          ? getProvinces(nextProps.addressOnlyInfo.country)
          : [],
        addressOnlyInfo: nextProps.addressOnlyInfo
      });
    }
  }

  render() {
    const { isBusinessRegistration, mode, isBusinessInfo, adminState, authState } = this.props;
    const { addressOnlyInfo, countriesList, provincesList, addressOnlyInfoError } = this.state;

    return (
      <div>
        <div className="row ma2">
          <div className="col-12">
            <div className="input-group mb-2 mr-sm-2 mb-sm-0">
              <TextField
                fullWidth={true}
                floatingLabelText="Address"
                floatingLabelFixed={true}
                value={addressOnlyInfo.address}
                onChange={this.onAddressChange}
                disabled={isBusinessInfo && adminState && authState ?
                  cannotWrite(BUSINESS, authState, adminState) && cannotUpdate(BUSINESS, authState, adminState) :
                  mode === 'DISPLAY'}
                style={(mode === 'DISPLAY') ? fieldStyles.rootStyle : {}}
                inputStyle={(mode === 'DISPLAY') ? fieldStyles.textStyle : {}}
                floatingLabelStyle={(mode === 'DISPLAY') ? fieldStyles.textStyle : {}}
              />
            </div>
          </div>
        </div>
        <div className="row">
          <div className="col-md-4 col-sm-12 col-xs-12">
            <TextField
              fullWidth={true}
              floatingLabelText="City"
              floatingLabelFixed={true}
              value={addressOnlyInfo.city}
              errorText={this.state.addressOnlyInfoError.cityError}
              onChange={this.onCityChange}
              disabled={isBusinessInfo && adminState && authState ?
                cannotWrite(BUSINESS, authState, adminState) && cannotUpdate(BUSINESS, authState, adminState) :
                mode === 'DISPLAY'}
              style={(mode === 'DISPLAY') ? fieldStyles.rootStyle : {}}
              inputStyle={(mode === 'DISPLAY') ? fieldStyles.textStyle : {}}
              floatingLabelStyle={(mode === 'DISPLAY') ? fieldStyles.textStyle : {}}
            />
          </div>
          <div className="col-md-4 col-sm-12 col-xs-12">
            <SelectField
              fullWidth={true}
              floatingLabelText={DEFAULT_COUNTRY_SELECTION}
              floatingLabelFixed={true}
              value={addressOnlyInfo.country}
              onChange={this.onCountryChange}
              disabled={isBusinessInfo && adminState && authState ?
                cannotWrite(BUSINESS, authState, adminState) && cannotUpdate(BUSINESS, authState, adminState) :
                mode === 'DISPLAY'}
              style={(mode === 'DISPLAY') ? fieldStyles.rootStyle : {}}
              labelStyle={(mode === 'DISPLAY') ? fieldStyles.textStyle : {}}
              floatingLabelStyle={(mode === 'DISPLAY') ? fieldStyles.textStyle : {}}
            >
              {
                countriesList.map((country, index) => {
                  return <MenuItem key={index} value={country.code} primaryText={country.name} />;
                })
              }
            </SelectField>
          </div>
          <div className="col-md-4 col-sm-12 col-xs-12">
            <SelectField
              fullWidth={true}
              floatingLabelText={DEFAULT_PROVINCE_SELECTION}
              floatingLabelFixed={true}
              value={addressOnlyInfo.province}
              onChange={this.onProvinceChange}
              disabled={isBusinessInfo && adminState && authState ?
                cannotWrite(BUSINESS, authState, adminState) && cannotUpdate(BUSINESS, authState, adminState) :
                mode === 'DISPLAY'}
              style={(mode === 'DISPLAY') ? fieldStyles.rootStyle : {}}
              labelStyle={(mode === 'DISPLAY') ? fieldStyles.textStyle : {}}
              floatingLabelStyle={(mode === 'DISPLAY') ? fieldStyles.textStyle : {}}
            >
              {
                provincesList.map((province, index) => {
                  return <MenuItem key={index} value={province.code} primaryText={province.name} />;
                })
              }
            </SelectField>
          </div>
        </div>
        <div className="row">
          <div className="col-md-4 col-sm-12 col-xs-12">
            <TextField
              fullWidth={true}
              floatingLabelText="Postal / Zip Code"
              floatingLabelFixed={true}
              errorText={addressOnlyInfoError.postalCodeError}
              value={formatPostalCode(this.state.addressOnlyInfo.postalCode)}
              onChange={this.onPostalCodeChange}
              disabled={isBusinessInfo && adminState && authState ?
                cannotWrite(BUSINESS, authState, adminState) && cannotUpdate(BUSINESS, authState, adminState) :
                mode === 'DISPLAY'}
              style={(this.props.mode === 'DISPLAY') ? fieldStyles.rootStyle : {}}
              inputStyle={(this.props.mode === 'DISPLAY') ? fieldStyles.textStyle : {}}
              floatingLabelStyle={(this.props.mode === 'DISPLAY') ? fieldStyles.textStyle : {}}
            />
          </div>
        </div>
      </div>
    );
  }

  private onAddressChange = (event: React.FormEvent<HTMLInputElement>, newValue: string): void =>
    this.setState(
      {
        addressOnlyInfo: {
          ...this.state.addressOnlyInfo,
          address: newValue
        }
      },
      this.addressOnlyInfoChangeHandler
    )

  private onCityChange = (event: React.FormEvent<HTMLInputElement>): void => {
    const newCity = event.currentTarget.value;
    let newAddressOnlyInfo = this.state.addressOnlyInfo;
    newAddressOnlyInfo.city = newCity;

    this.setState(
      {
        addressOnlyInfo: newAddressOnlyInfo,
        addressOnlyInfoError: {
          ...this.state.addressOnlyInfoError,
          cityError: newCity.length > MAX_LENGTH_FOR_DB ? 'City name is too long' : ''
        }
      },
      this.addressOnlyInfoChangeHandler
    );
  }

  private onCountryChange = (event: React.TouchEvent<HTMLSelectElement>, index: number, value: CountryCode): void => {
    const newCountry = value;
    let newAddressOnlyInfo = this.state.addressOnlyInfo;
    let newAddressOnlyInfoError = this.state.addressOnlyInfoError;
    newAddressOnlyInfo.country = newCountry;
    newAddressOnlyInfo.province = '';
    newAddressOnlyInfo.postalCode = '';
    newAddressOnlyInfoError.postalCodeError = '';

    this.setState(
      {
        addressOnlyInfo: newAddressOnlyInfo,
        addressOnlyInfoError: newAddressOnlyInfoError,
        provincesList: getProvinces(newCountry)
      },
      this.addressOnlyInfoChangeHandler
    );
  }

  private onProvinceChange = (event: React.TouchEvent<HTMLSelectElement>, index: number, value: ProvinceCode): void =>
    this.setState(
      {
        addressOnlyInfo: {
          ...this.state.addressOnlyInfo,
          province: value
        }
      },
      this.addressOnlyInfoChangeHandler
    )

  private onPostalCodeChange(event: React.FormEvent<HTMLInputElement>) {
    const newPostalCode = formatPostalCode(event.currentTarget.value);
    let newAddressOnlyInfo = this.state.addressOnlyInfo;
    let newAddressOnlyInfoError = this.state.addressOnlyInfoError;
    newAddressOnlyInfo.postalCode = newPostalCode;
    newAddressOnlyInfoError.postalCodeError = isValidPostalCode(
      newPostalCode, this.state.addressOnlyInfo.country, 'Please enter a valid postal / zip code'
    );

    this.setState(
      {
        addressOnlyInfo: newAddressOnlyInfo,
        addressOnlyInfoError: newAddressOnlyInfoError
      },
      this.addressOnlyInfoChangeHandler
    );
  }

  private addressOnlyInfoChangeHandler() {
    const { addressOnlyInfoError, addressOnlyInfo } = this.state;
    const { addressOnlyInfoChangeHandler } = this.props;

    if (addressOnlyInfoChangeHandler) {
      addressOnlyInfoChangeHandler(addressOnlyInfo, addressOnlyInfoError);
    }
  }
}

export default AddressOnlyComponent;


// WEBPACK FOOTER //
// ./src/geo/AddressOnlyComponent.tsx